import React from 'react';
import { useTranslation } from 'react-i18next';
import type { UseClientsListReturn } from '../../hooks/useClientsList';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Search, Eye, Calendar, Mail, User } from 'lucide-react';
import { format } from 'date-fns/format';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { Badge } from '../ui/badge';

export const ClientsList: React.FC<UseClientsListReturn> = ({
  loading,
  searchName,
  setSearchName,
  searchEmail,
  setSearchEmail,
  registeredAfter,
  setRegisteredAfter,
  registeredBefore,
  setRegisteredBefore,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  filteredClients,
  paginatedItems,
  totalPages,
  isModalOpen,
  setIsModalOpen,
  selectedClient,
  loadingClientDetails,
  handleViewDetails,
}) => {
  const { t } = useTranslation();

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'quoted':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl tracking-tight">{t('admin.clients.title')}</h2>
      </div>

      <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-lg border shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <input
            type="text"
              placeholder={t('admin.clients.filterName')}
            className="pl-9 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>

        <div className="relative flex-1">
          <Mail className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <input
            type="text"
              placeholder={t('admin.clients.filterEmail')}
            className="pl-9 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <div className="w-40">
            <input
              type="date"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={registeredAfter}
              onChange={(e) => setRegisteredAfter(e.target.value)}
              placeholder={t('admin.clients.registeredAfter')}
              title={t('admin.clients.registeredAfter')}
            />
          </div>
          <div className="w-40">
            <input
              type="date"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              value={registeredBefore}
              onChange={(e) => setRegisteredBefore(e.target.value)}
              placeholder={t('admin.clients.registeredBefore')}
              title={t('admin.clients.registeredBefore')}
            />
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.clients.name')}</TableHead>
                <TableHead>{t('admin.clients.email')}</TableHead>
                <TableHead>{t('admin.clients.quoteCount')}</TableHead>
                <TableHead>{t('admin.clients.registeredAt')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10">
                    {t('admin.clients.loading')}
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                    {t('admin.clients.noClients')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell>{client.email}</TableCell>
                    <TableCell>{client.quotes_count}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {format(new Date(client.created_at), 'PPP')}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleViewDetails(client)}
                        title={t('admin.clients.viewDetails')}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.items_per_page')}:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.page_info', { current: currentPage, total: totalPages || 1 })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p: number) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('admin.clients.viewDetails')}</DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                    <User className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">{t('admin.clients.name')}</p>
                    <p>{selectedClient.name}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">{t('admin.clients.email')}</p>
                    <p>{selectedClient.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                    <Calendar className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">{t('admin.clients.registeredAt')}</p>
                    <p>{format(new Date(selectedClient.created_at), 'PPP')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                    <span className="text-lg">#</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">{t('admin.clients.totalQuotes')}</p>
                    <p>{selectedClient.quotes_count}</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="mb-3">{t('admin.clients.recentQuotes')}</h3>
                {loadingClientDetails ? (
                  <p className="text-center py-4 text-muted-foreground">{t('admin.clients.loading')}</p>
                ) : !selectedClient.recent_quotes || selectedClient.recent_quotes.length === 0 ? (
                  <p className="text-center py-4 text-muted-foreground border rounded-md border-dashed">
                    {t('admin.clients.no_quotes_for_client')}
                  </p>
                ) : (
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('admin.quotes.columns.tracking')}</TableHead>
                          <TableHead>{t('admin.quotes.columns.route')}</TableHead>
                          <TableHead>{t('admin.quotes.columns.status')}</TableHead>
                          <TableHead>{t('admin.quotes.columns.date')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedClient.recent_quotes.map((quote: any) => (
                          <TableRow key={quote.id}>
                            <TableCell className="font-medium">{quote.tracking_number}</TableCell>
                            <TableCell>
                              <div className="flex items-center text-sm">
                                <span>{quote.origin_country}</span>
                                <span className="mx-2 text-muted-foreground">→</span>
                                <span>{quote.destination_country}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant={getStatusBadgeVariant(quote.status)}>
                                {t(`admin.quotes.status.${quote.status}`, { defaultValue: quote.status })}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDistanceToNow(new Date(quote.created_at), { addSuffix: true })}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setIsModalOpen(false)}>
              {t('admin.dashboard.close')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
